import React, { useState, useEffect } from "react";
import { Layout, Table, Button, InputNumber, Card, message, Typography, Space, Tag, Spin, Descriptions } from "antd";
import { TruckOutlined, CloseCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Text } = Typography;

const RequestAccept = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [request, setRequest] = useState(null);
    const [issuingItems, setIssuingItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchRequestDetail();
    }, [id]);

    const fetchRequestDetail = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/inventory/material_request/accept_detail.php?mr_id=${id}`);
            if (response.data.status === "success" && response.data.dataset) {
                const reqData = response.data.dataset;
                setRequest(reqData);

                // Initialize issue quantities with remain_qty (or current stock if lower)
                const itemsToIssue = (reqData.items || []).map(item => {
                    const remain = Number(item.remain_qty || 0);
                    const stock = Number(item.current_stock || 0);
                    return {
                        ...item,
                        new_issue_qty: remain > 0 ? Math.min(remain, stock) : 0
                    };
                });
                setIssuingItems(itemsToIssue);
            } else {
                message.error("Failed to load request details.");
                navigate("/inventory/material-request/accepts");
            }
        } catch (error) {
            message.error("Error fetching request details.");
            navigate("/inventory/material-request/accepts");
        } finally {
            setLoading(false);
        }
    };

    const handleQtyChange = (mri_id, val) => {
        setIssuingItems(prev => prev.map(item => {
            if (item.mri_id === mri_id) {
                return { ...item, new_issue_qty: val };
            }
            return item;
        }));
    };

    const handleConfirmIssue = async () => {
        // Validate quantities
        const invalidItems = issuingItems.filter(item => {
            const qty = Number(item.new_issue_qty || 0);
            return qty > Number(item.remain_qty) || qty > Number(item.current_stock);
        });

        if (invalidItems.length > 0) {
            message.error("Some accept quantities exceed either remaining quantity or warehouse stock.");
            return;
        }

        const totalToIssue = issuingItems.reduce((sum, item) => sum + Number(item.new_issue_qty || 0), 0);
        if (totalToIssue <= 0) {
            message.warning("Please enter an accept quantity greater than 0 for at least one item.");
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                mr_id: Number(id),
                items: issuingItems.map(item => ({
                    mri_id: Number(item.mri_id),
                    issue_qty: Number(item.new_issue_qty || 0)
                }))
            };

            const response = await api.post("/inventory/material_request/accept.php", payload);
            if (response.data.status === "success") {
                message.success(response.data.message || "Material request items successfully accepted.");
                navigate("/inventory/material-request/accepts");
            } else {
                message.error(response.data.message || "Failed to accept items.");
            }
        } catch (error) {
            message.error("Error accepting material request items.");
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: "Item Code",
            dataIndex: "in_code",
            key: "in_code"
        },
        {
            title: "Item Name",
            dataIndex: "in_name",
            key: "in_name"
        },
        {
            title: "UOM",
            dataIndex: "uom_name",
            key: "uom_name",
            render: (text) => text || "-"
        },
        {
            title: "Approved Qty",
            dataIndex: "accept_qty",
            key: "accept_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Already Accepted",
            dataIndex: "issue_qty",
            key: "issue_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Remaining Qty",
            dataIndex: "remain_qty",
            key: "remain_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Wh Stock",
            dataIndex: "current_stock",
            key: "current_stock",
            align: 'right',
            render: (qty) => {
                const stockVal = parseFloat(qty || 0);
                return <span style={{ color: stockVal === 0 ? 'red' : 'inherit', fontWeight: stockVal === 0 ? '600' : 'normal' }}>{stockVal.toFixed(2)}</span>;
            }
        },
        {
            title: "Accept Now",
            key: "issue_now",
            width: 160,
            render: (_, record) => {
                const maxAllowed = Math.min(Number(record.remain_qty), Number(record.current_stock));
                return (
                    <InputNumber
                        min={0}
                        max={maxAllowed}
                        step={0.01}
                        value={record.new_issue_qty}
                        onChange={(val) => handleQtyChange(record.mri_id, val)}
                        style={{ width: '100%' }}
                    />
                );
            }
        }
    ];

    if (loading || !request) {
        return (
            <Layout className="fluid-page">
                <Headerdiv />
                <Content className="fluid-content" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
                    <Spin size="large" tip="Loading request details..." />
                </Content>
                <Footerdiv />
            </Layout>
        );
    }

    const displayStatus = request.req_status;

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Accept Items', link: '/inventory/material-request/accepts' },
                    { label: `Accept - ${request.mr_ref}` },
                ]} />
                <h1 className="fluid-page-title">
                    <TruckOutlined style={{ marginRight: 12 }} />Accept Request Items
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card" title="Request Overview" style={{ marginBottom: 24 }}>
                    <Descriptions bordered column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} size="small">
                        <Descriptions.Item label="Reference Number"><strong>{request.mr_ref}</strong></Descriptions.Item>
                        <Descriptions.Item label="Status"><Tag color={getStatusColor(displayStatus)}>{displayStatus}</Tag></Descriptions.Item>
                        <Descriptions.Item label="Required Date">{dayjs(request.required_date).format("YYYY-MM-DD")}</Descriptions.Item>
                        <Descriptions.Item label="Requested By">
                            {request.creator_name ? request.creator_name.charAt(0).toUpperCase() + request.creator_name.slice(1) : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Requested Date">
                            {request.req_dt ? dayjs(request.req_dt).format("YYYY-MM-DD hh:mm:ss A") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approved By">
                            {request.reviewer_name ? request.reviewer_name.charAt(0).toUpperCase() + request.reviewer_name.slice(1) : "System"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approved Date">
                            {request.req_approved_dt ? dayjs.unix(Number(request.req_approved_dt)).format("YYYY-MM-DD hh:mm:ss A") : "-"}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card className="fluid-card" title="Items to Accept">
                    <Table
                        dataSource={issuingItems}
                        columns={columns}
                        rowKey="mri_id"
                        pagination={false}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        style={{ marginBottom: 24 }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <Button 
                            danger
                            icon={<CloseCircleOutlined />} 
                            onClick={() => navigate("/inventory/material-request/accepts")}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<CheckCircleOutlined />} 
                            onClick={handleConfirmIssue}
                            loading={submitting}
                            className="fluid-btn-primary"
                        >
                            Confirm Accept
                        </Button>
                    </div>
                </Card>
            </Content>

            <Footerdiv />
        </Layout>
    );
};

export default RequestAccept;
